import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { notificationsApi } from '../lib/api';
import { Menu, X, User, LogOut, LayoutDashboard, FileText, Settings, Bell, Building2, ChevronDown } from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';
import { Button } from './ui/button';

const Navbar = ({ transparent = false }) => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const [totalCount, setTotalCount] = useState(0);
  const { user, isAuthenticated, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const fetchNotifCount = async () => {
      try {
        const res = await notificationsApi.getAll();
        setUnreadCount(res.data.unread_count || 0);
        setTotalCount(res.data.total_count || res.data.notifications?.length || 0);
      } catch (e) {}
    };
    if (isAuthenticated) {
      fetchNotifCount();
      const interval = setInterval(fetchNotifCount, 30000);
      return () => clearInterval(interval);
    }
  }, [isAuthenticated]);

  const isLanding = location.pathname === '/';

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  const getDashboardLink = () => {
    if (user?.role === 'admin') return '/admin';
    if (user?.role === 'employee') return '/employee';
    return '/dashboard';
  };

  const navLinks = [
    { name: 'Countries', href: '/countries' },
    { name: 'Visa Types', href: '/visa-types' },
    { name: 'How It Works', href: '/#how-it-works' },
    { name: 'Contact', href: '/contact' },
  ];

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 bg-white shadow-sm border-b border-[#E8E2D6]`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16 lg:h-20">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-2" data-testid="navbar-logo">        
              <img src="/images/logo.png" alt="EasyMyVisa Logo" className=""  style={{ width: '250px', height: '150px' }} />
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center gap-8">
            {navLinks.map((link) => (
              <Link
                key={link.name}
                to={link.href}
                className="text-[#0A1E40]/80 hover:text-[#0A1E40] font-medium transition-colors"
                data-testid={`nav-link-${link.name.toLowerCase().replace(' ', '-')}`}
              >
                {link.name}
              </Link>
            ))}
          </div>

          {/* Auth Buttons */}
          <div className="hidden lg:flex items-center gap-3">
            {isAuthenticated ? (
              <>
                {/* Notification Bell */}
                <Link 
                  to="/notifications"
                  className="relative p-2 rounded-full hover:bg-[#0A1E40]/5 transition-colors"
                  data-testid="navbar-notification-bell"
                >
                  <Bell className="w-5 h-5 text-[#0A1E40]" />
                  {unreadCount > 0 && (
                    <span className="absolute -top-0.5 -right-0.5 bg-[#FF5A36] text-white text-[10px] font-bold min-w-[18px] h-[18px] flex items-center justify-center rounded-full px-1" data-testid="navbar-notif-badge">
                      {unreadCount > 9 ? '9+' : unreadCount}
                    </span>
                  )}
                </Link>

                {/* User Menu */}
                <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    variant="ghost"
                    className="flex items-center gap-2 hover:bg-[#0A1E40]/5"
                    data-testid="user-menu-trigger"
                  >
                    <div className="w-8 h-8 bg-[#0A1E40] rounded-full flex items-center justify-center">
                      <span className="text-white text-sm font-semibold">
                        {user?.name?.charAt(0)?.toUpperCase() || 'U'}
                      </span>
                    </div>
                    <span className="font-medium text-[#0A1E40]">{user?.name?.split(' ')[0]}</span>
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56">
                  <div className="px-3 py-2">
                    <p className="font-semibold text-[#0A1E40]">{user?.name}</p>
                    <p className="text-sm text-[#4A5568]">{user?.email}</p>
                  </div>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem asChild>
                    <Link to={getDashboardLink()} className="flex items-center gap-2" data-testid="menu-dashboard">
                      <LayoutDashboard className="w-4 h-4" />
                      Dashboard
                    </Link>
                  </DropdownMenuItem>
                  <DropdownMenuItem asChild>
                    <Link to="/my-applications" className="flex items-center gap-2" data-testid="menu-applications">
                      <FileText className="w-4 h-4" />
                      My Applications
                    </Link>
                  </DropdownMenuItem>
                  <DropdownMenuItem asChild>
                    <Link to="/profile" className="flex items-center gap-2" data-testid="menu-profile">
                      <User className="w-4 h-4" />
                      Profile
                    </Link>
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem
                    onClick={handleLogout}
                    className="text-red-600 focus:text-red-600"
                    data-testid="menu-logout"
                  >
                    <LogOut className="w-4 h-4 mr-2" />
                    Logout
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
              </>
            ) : (
              <>
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="ghost" className="text-[#0A1E40] hover:bg-[#0A1E40]/5" data-testid="nav-login-btn">
                      Login <ChevronDown className="w-4 h-4 ml-1" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="w-48">
                    <DropdownMenuItem asChild>
                      <Link to="/login" state={{ from: location }} className="flex items-center gap-2 cursor-pointer" data-testid="login-candidate">
                        <User className="w-4 h-4" /> Candidate
                      </Link>
                    </DropdownMenuItem>
                    <DropdownMenuItem asChild>
                      <Link to="/partner/login" className="flex items-center gap-2 cursor-pointer" data-testid="login-partner">
                        <Building2 className="w-4 h-4" /> Partner
                      </Link>
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
                <Link to="/partner">
                  <Button variant="outline" className="border-[#FF5A36] text-[#FF5A36] hover:bg-[#FF5A36]/5 rounded-full px-6" data-testid="nav-partner-btn">
                    Partner
                  </Button>
                </Link>
                <Link to="/register">
                  <Button className="bg-[#FF5A36] hover:bg-[#E0492B] text-white rounded-full px-6" data-testid="nav-register-btn">
                    Get Started
                  </Button>
                </Link>
              </>
            )}
          </div>

          {/* Mobile Menu Button */}
          <button
            className="lg:hidden p-2"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            data-testid="mobile-menu-btn"
          >
            {mobileMenuOpen ? (
              <X className="w-6 h-6 text-[#0A1E40]" />
            ) : (
              <Menu className="w-6 h-6 text-[#0A1E40]" />
            )}
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      {mobileMenuOpen && (
        <div className="lg:hidden bg-white border-t border-[#D1CBBF]">
          <div className="px-4 py-4 space-y-3">
            {navLinks.map((link) => (
              <Link
                key={link.name}
                to={link.href}
                className="block px-4 py-2 text-[#0A1E40] hover:bg-[#F5F0E6] rounded-lg"
                onClick={() => setMobileMenuOpen(false)}
              >
                {link.name}
              </Link>
            ))}
            <div className="border-t border-[#D1CBBF] pt-3 mt-3">
              {isAuthenticated ? (
                <>
                  <Link
                    to="/notifications"
                    className="flex items-center justify-between px-4 py-2 text-[#0A1E40] hover:bg-[#F5F0E6] rounded-lg"
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    <span className="flex items-center gap-2">
                      <Bell className="w-4 h-4" /> Notifications
                    </span>
                    {unreadCount > 0 && (
                      <span className="bg-[#FF5A36] text-white text-xs font-bold px-2 py-0.5 rounded-full">
                        {unreadCount}
                      </span>
                    )}
                  </Link>
                  <Link
                    to={getDashboardLink()}
                    className="block px-4 py-2 text-[#0A1E40] hover:bg-[#F5F0E6] rounded-lg"
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    Dashboard
                  </Link>
                  <button
                    onClick={() => {
                      handleLogout();
                      setMobileMenuOpen(false);
                    }}
                    className="block w-full text-left px-4 py-2 text-red-600 hover:bg-red-50 rounded-lg"
                  >
                    Logout
                  </button>
                </>
              ) : (
                <div className="space-y-2">
                  <p className="text-xs text-[#9CA3AF] font-semibold uppercase tracking-wider px-1">Login as</p>
                  <div className="flex gap-2">
                    <Link to="/login" state={{ from: location }} className="flex-1" onClick={() => setMobileMenuOpen(false)}>
                      <Button variant="outline" className="w-full"><User className="w-4 h-4 mr-1" /> Candidate</Button>
                    </Link>
                    <Link to="/partner/login" className="flex-1" onClick={() => setMobileMenuOpen(false)}>
                      <Button variant="outline" className="w-full border-[#FF5A36] text-[#FF5A36]"><Building2 className="w-4 h-4 mr-1" /> Partner</Button>
                    </Link>
                  </div>
                  <Link to="/register" className="block" onClick={() => setMobileMenuOpen(false)}>
                    <Button className="w-full bg-[#FF5A36] hover:bg-[#E0492B]">Get Started</Button>
                  </Link>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </nav>
  );
};

export default Navbar;
